import React from "react";
import {
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { Tooltip } from "react-tooltip";

const PlotOptions = ({ plotType, settings, onUpdateSetting }) => {
  const InfoTooltip = ({ id, content, placement = "top" }) => (
    <>
      <AiOutlineInfoCircle
        data-tooltip-id={id}
        title={content}
        style={{
          marginLeft: "4px",
          cursor: "help",
          color: "#6B7280",
          fontSize: "16px",
          verticalAlign: "middle",
          display: "inline-flex",
          alignItems: "center",
        }}
      />
    </>
  );

  const inputStyle = {
    borderRadius: "6px",
    border: "1px solid #d1d5db",
    fontSize: "14px",
    padding: "8px 12px",
    backgroundColor: "#ffffff",
  };


  const NucleotideBounds = ({ settings, onUpdateSetting }) => {
    const ANCHOR_OPTIONS = [
      { value: "tx_start", label: "Transcript start" },
      { value: "cds_start", label: "CDS start" },
      { value: "cds_stop",  label: "CDS stop" },
      { value: "tx_end",    label: "Transcript end" },
    ];

    const AnchorSelect = ({ id, value }) => (
      <Input
        type="select"
        id={id}
        value={value || "tx_start"}
        onChange={(e) => onUpdateSetting(id, e.target.value)}
        bsSize="sm"
        style={{ minWidth: "100px", fontSize: "13px" }}
      >
        {ANCHOR_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Input>
    );

    const NucleotideInput = ({ id, value, placeholder = "0" }) => {
      const handleKeyDown = (e) => {
        if (e.key === "Backspace" && (value === "" || value == null)) {
          e.preventDefault();
          e.stopPropagation();
        }
      };
      return (
        <Input
          type="number"
          id={id}
          min={-10000}
          value={value ?? ""}
          onChange={(e) => onUpdateSetting(id, e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          bsSize="sm"
          style={{ minWidth: "60px", fontSize: "13px" }}
        />
      );
    };

    const LimitRow = ({ rowLabel, anchorId, signId, valueId, tooltipId }) => (
      <Row className="align-items-center mb-3">
        <Col lg={3} md={2} className="mb-2 mb-md-0">
          <Label htmlFor={anchorId} className="mb-0" style={{ fontWeight: 500 }}>
            {rowLabel}
            {typeof InfoTooltip !== "undefined" && (
              <InfoTooltip
                id={tooltipId}
                content={`${rowLabel}: Choose the min and max limits of where the sequence will begin/end, optionally include an offset, this can be positive or negative.`}
              />
            )}
          </Label>
        </Col>

        <Col xs={12} md={4} className="mb-2 mb-md-0">
          <AnchorSelect id={anchorId} value={settings[anchorId]} />
        </Col>

        <Col xs={12} md={4}>
          <div className="d-flex align-items-center gap-2">
            <NucleotideInput id={valueId} value={settings[valueId]} />
            <span style={{ fontSize: "13px", color: "#6b7280" }}>nucleotides</span>
          </div>
        </Col>
      </Row>
    );

    return (
      <div className="border rounded" style={{ padding: "16px", backgroundColor: "#fff" }}>
        <h6
          className="mb-3"
          style={{ fontSize: "14px", fontWeight: 500, color: "#374151" }}
        >
          Min/Max Limits
        </h6>

        {/* Header */}
        <Row
          className="align-items-center mb-3 pb-2"
          style={{ borderBottom: "1px solid #e5e7eb" }}
        >
          <Col xs={12} md={4}>
            <strong style={{ fontSize: "12px", color: "#6b7280" }}>Limit</strong>
          </Col>
          <Col xs={12} md={4}>
            <strong style={{ fontSize: "12px", color: "#6b7280" }}>Anchor</strong>
          </Col>
          <Col xs={12} md={4}>
            <strong style={{ fontSize: "12px", color: "#6b7280" }}>Offset</strong>
          </Col>
        </Row>

        <LimitRow
          rowLabel="Min Limit"
          anchorId="min_limit_anchor"
          valueId="min_limit_val"
          tooltipId="min-limit-tooltip"
        />

        <LimitRow
          rowLabel="Max Limit"
          anchorId="max_limit_anchor"
          valueId="max_limit_val"
          tooltipId="max-limit-tooltip"
        />
      </div>
    );
  };

  // Single Transcript Nucleotide Composition
  const renderNucCompSingleOptions = () => (
    <Row>
      <Col md={12}>
        <FormGroup className="mb-3">
          <Label for="singleNucTran">
            Transcript:
            <InfoTooltip
              id="tt-single-transcript"
              content="Enter the transcript ID of the transcript you want to view."
            />
          </Label>
          <Input
            id="singleNucTran"
            type="text"
            value={settings.singleNucTran || ""}
            onChange={(e) => onUpdateSetting("singleNucTran", e.target.value)}
            placeholder="Enter transcript ID"
            style={inputStyle}
          />
        </FormGroup>
      </Col>
    </Row>
  );

  // Gene Count (No options)
  const renderGeneCountOptions = () => (
    <div className="text-center text-muted py-5">
      <h5>Gene Count Analysis</h5>
      <p className="small">
        No additional configuration required - click "View Plot" to generate the
        analysis.
      </p>
    </div>
  );

  // Codon Usage
  const renderCodonUsageOptions = () => (
    <Row>
      <Col md={12}>
        <FormGroup className="mb-3">
          <Label>
            Transcript list:
            <InfoTooltip
              id="tt-codon-transcript-list"
              content="Enter a list of genes or transcript IDs separated by commas or spaces or on separate lines, this can also be copy/pasted directly from an excel sheet. If genes are entered the files returned will be based on the principal transcript of each gene. The list needs to consist of only transcript IDs or only gene names, mixing transcript ID's and gene names will result in an error."
            />
          </Label>
          <Input
            type="textarea"
            rows="4"
            value={settings.customTranInput || ""}
            onChange={(e) => onUpdateSetting("customTranInput", e.target.value)}
            placeholder="Enter transcript IDs (comma separated) or leave empty for all principal isoforms"
            style={inputStyle}
          />
        </FormGroup>
      </Col>
    </Row>
  );

  // ORF Table - Organized into logical sections
  const renderOrfStatsOptions = () => (
    <Row>
      <Col md={6}>
        {/* General Options */}
        <h6 className="mb-3 text-muted">General Options</h6>
        <FormGroup check className="mb-3">
          <Input
            type="checkbox"
            checked={settings.principal || false}
            onChange={(e) => onUpdateSetting("principal", e.target.checked)}
            className="form-check-input"
            id="principal"
          />
          <Label check for="principal">
            Select principal isoforms only
            <InfoTooltip
              id="tt-orf-principal"
              content="If checked, results will be limited to only principal isoforms, otherwise all transcripts will be used."
            />
          </Label>
        </FormGroup>
      </Col>

      <Col md={6}>
        {/* ORF Type Selection */}
        <h6 className="mb-3 text-muted">ORF Type</h6>
        <FormGroup className="mb-3">
          <Label>
            <strong>Select ORF Type:</strong>
            <InfoTooltip id="tt-orf-type" content="Select ORF type." />
          </Label>
          <div className="mt-2">
            {[
              { value: "uorf", label: "uORF" },
              { value: "ouorf", label: "ouORF" },
              { value: "extension", label: "N-terminal extension" },
              { value: "cds", label: "CDS" },
              { value: "nested", label: "Nested" },
              { value: "odorf", label: "oDORF" },
              { value: "dorf", label: "dORF" },
              { value: "noncoding", label: "noncoding" },
            ].map((option) => (
              <FormGroup check key={option.value}>
                <Input
                  type="radio"
                  name="orfType"
                  value={option.value}
                  checked={settings.orfType === option.value}
                  onChange={(e) => onUpdateSetting("orfType", e.target.value)}
                  className="form-check-input"
                  id={`orf-${option.value}`}
                />
                <Label check for={`orf-${option.value}`}>
                  {option.label}
                </Label>
              </FormGroup>
            ))}
          </div>
        </FormGroup>
      </Col>
    </Row>
  );

  // Fetch Sequences - Organized into sections
  const renderFetchSeqOptions = () => (
    <Row>
      <Col md={6}>
        <NucleotideBounds
          settings={settings}
          onUpdateSetting={onUpdateSetting}
        />
      </Col>

      <Col md={6}>

        {/* Transcripts */}
        <FormGroup className="mb-3">
          <Label>Transcripts:</Label>
          <div className="mt-2">
            {[
              { value: "prin_trans", label: "Principal" },
              { value: "all", label: "All" },
              { value: "custom_trans", label: "Custom" },
            ].map((option) => (
              <FormGroup
                check
                key={option.value}
                className="form-check-inline"
              >
                <Input
                  type="radio"
                  name="transcriptInput"
                  value={option.value}
                  checked={settings.transcriptInput === option.value}
                  onChange={(e) =>
                    onUpdateSetting("transcriptInput", e.target.value)
                  }
                  className="form-check-input"
                />
                <Label check>{option.label}</Label>
              </FormGroup>
            ))}
          </div>
        </FormGroup>

        {/* Transcript Selection */}
        {settings.transcriptInput === "custom_trans" && (
          <FormGroup className="mb-3">
            <Label>
              Gene/Transcript list:{" "}
              <InfoTooltip
                id="tt-fetch-transcript-list"
                content="Enter a list of genes or transcript IDs separated by commas or spaces or on separate lines, this can also be copy/pasted directly from an excel sheet. If genes are entered the files returned will be based on the principal transcript of each gene. The list needs to consist of only transcript IDs or only gene names, mixing transcript ID's and gene names will result in an error."
              />
            </Label>
            <Input
              type="textarea"
              rows="4"
              value={settings.customTranInput || ""}
              onChange={(e) => onUpdateSetting("customTranInput", e.target.value)}
              placeholder="Enter transcript IDs (comma separated)"
              style={inputStyle}
            />
          </FormGroup>
        )}

      </Col>
    </Row>
  );

  const renderOptions = () => {
    switch (plotType) {
      case "nuc_comp_single":
        return renderNucCompSingleOptions();
      case "fetch_seq":
        return renderFetchSeqOptions();
      case "gene_count":
        return renderGeneCountOptions();
      case "orfstats":
        return renderOrfStatsOptions();
      case "codon_usage":
        return renderCodonUsageOptions();
      default:
        return (
          <div className="text-center text-muted py-4">
            Please select a plot type to configure options
          </div>
        );
    }
  };

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0" style={{ color: "#1a6d51" }}>
          <i className="ri-settings-3-line me-2"></i>
          Plot Options
        </h5>
      </CardHeader>
      <CardBody>{renderOptions()}</CardBody>
    </Card>
  );
};

export default PlotOptions;
